import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription } from 'rxjs';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { InvoiceService } from 'src/app/services/invoice.service';
import { WarehouseInvoiceService } from 'src/app/services/warehouse-invoice.service';
import { WarehouseService } from 'src/app/services/warehouse.service';

/**
 * Panel "Listado de facturas": tarjetas de resumen, filtros y grid.
 *
 * Vive aparte de invoicing-list porque la pantalla de Facturas de almacén
 * muestra exactamente el mismo panel en una de sus pestañas.
 */
@Component({
    selector: 'app-invoices-panel',
    templateUrl: './invoices-panel.component.html',
    styleUrls: ['./invoices-panel.component.css']
})
export class InvoicesPanelComponent implements OnInit, OnDestroy {
    /** El saldo de timbres sólo aplica al facturador, no a la vista de almacén. */
    @Input() showStampBalance = true;

    /**
     * De dónde salen las filas.
     *
     * `api` es el listado real (`invoice/list`). `warehouse` es data de prueba
     * acotada a facturas de servicios de almacén: el backend todavía no tiene
     * almacenes, así que no hay forma de pedirle ese subconjunto y `invoice/list`
     * devolvería también las de transporte.
     */
    @Input() source: 'api' | 'warehouse' = 'api';

    constructor(
        private invoiceService: InvoiceService,
        private warehouseInvoiceService: WarehouseInvoiceService,
        private warehouseService: WarehouseService,
        private spinner: NgxSpinnerService,
        private router: Router
    ) {}

    public stats = {
        total_amount: 0,
        issued_count: 0,
        pending_amount: 0,
        paid_count: 0,
        month: ''
    };
    public monthLabel = '';

    public stampBalance: {
        stamps_balance: number;
        is_unlimited: boolean;
        level: 'green' | 'yellow' | 'red';
    } | null = null;
    public stampBalanceLoading = false;

    public searchText = '';
    public createdAt = '';
    public createdAtDisplay = '';
    public statusFilter = '';
    public paymentStatusFilter = '';
    public paymentMethodFilter = '';

    private formatDate = (value: any): string => {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            const parts = String(value).split('-');
            if (parts.length === 3) return `${parts[2].substring(0, 2)}/${parts[1]}/${parts[0]}`;
            return String(value);
        }
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    columnDefs = [
        { field: 'folio', headerName: 'Folio', flex: 0.7 },
        {
            field: 'issued_at',
            headerName: 'Fecha de creación',
            flex: 1,
            valueFormatter: (p: any) => this.formatDate(p.value)
        },
        { field: 'client_name', headerName: 'Cliente', flex: 1.6 },
        {
            field: 'total',
            headerName: 'Total',
            flex: 1,
            valueFormatter: (p: any) => p.value != null
                ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p.value)
                : ''
        },
        {
            field: 'payment_method',
            headerName: 'Tipo de pago',
            flex: 1,
            valueFormatter: (p: any) => {
                const v = (p.value || '').toString().toUpperCase();
                if (v === 'PUE') return 'Completo';
                if (v === 'PPD') return 'Parcial';
                return v;
            }
        },
        {
            field: 'status',
            headerName: 'Estado Factura',
            flex: 1.1,
            cellRenderer: (p: any) => {
                const v = (p.value || '').toString();
                if (!v) return '';
                let cls = 'invoicing-badge invoicing-badge--neutral';
                let label = v;
                if (v === 'Cancelada') { cls = 'invoicing-badge invoicing-badge--danger'; label = 'Cancelado'; }
                else if (v === 'Pagada') { cls = 'invoicing-badge invoicing-badge--success'; label = 'Facturado'; }
                else if (v === 'Vigente') { cls = 'invoicing-badge invoicing-badge--info'; label = 'Pendiente'; }
                // Las que nacieron de una refacturación se marcan aparte del estado.
                const refactura = p.data?.is_refactura
                    ? '<span class="invoicing-badge invoicing-badge--warning ms-1">Refacturada</span>'
                    : '';
                return `<span class="${cls}">${label}</span>${refactura}`;
            }
        },
        {
            field: 'payment_status',
            headerName: 'Estado Pago',
            flex: 1.1,
            cellRenderer: (p: any) => {
                const v = (p.value || '').toString();
                if (!v) return '';
                let cls = 'invoicing-badge invoicing-badge--warning';
                let label = v;
                if (v === 'Pagado') { cls = 'invoicing-badge invoicing-badge--success'; label = 'Pagado'; }
                else if (v === 'Vencido') { cls = 'invoicing-badge invoicing-badge--danger'; label = 'No Pagado'; }
                else if (v === 'Pendiente') { cls = 'invoicing-badge invoicing-badge--warning'; label = 'Saldo Pendiente'; }
                return `<span class="${cls}">${label}</span>`;
            }
        },
        {
            headerName: 'Acciones',
            width: 150,
            sortable: false,
            filter: false,
            cellRenderer: (p: any) => {
                const folio = p.data?.folio;
                if (!folio) return '';
                const paymentStatus = (p.data?.payment_status || '').toString();
                const showPay = paymentStatus !== 'Pagado';
                return `
                    <div class="invoicing-actions">
                        <button type="button" class="invoicing-actions__btn" data-action="view" title="Ver factura" aria-label="Ver factura">
                            <i class="ti ti-eye"></i>
                        </button>
                        <button type="button" class="invoicing-actions__btn" data-action="download" title="Descargar factura" aria-label="Descargar factura">
                            <i class="ti ti-download"></i>
                        </button>
                        <button type="button" class="invoicing-actions__btn" data-action="detail" title="Detalle" aria-label="Detalle">
                            <i class="ti ti-list-check"></i>
                        </button>
                        ${showPay ? `
                        <button type="button" class="invoicing-actions__btn invoicing-actions__btn--accent" data-action="pay" title="Registrar pago" aria-label="Registrar pago">
                            <i class="ti ti-cash"></i>
                        </button>` : ''}
                    </div>
                `;
            },
            onCellClicked: (e: any) => {
                const target = e.event?.target as HTMLElement | null;
                const btn = target?.closest('button[data-action]') as HTMLButtonElement | null;
                const action = btn?.dataset['action'] || 'view';
                const folio = e.data?.folio;
                if (!folio) return;
                this.handleRowAction(action, folio, e.data);
            }
        }
    ];

    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams: any = { loadingMessage: 'Espere un momento...' };

    public defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
    };

    private gridApi!: GridApi;
    public rowData: any[] = [];

    /** Almacén activo en modo `warehouse`; null hasta la primera emisión. */
    private warehouseName: string | null = null;
    private warehouseSub?: Subscription;

    /** Qué servicio de almacén se factura: es lo que hace legible que el
     *  listado sean sólo facturas de almacén y no del facturador general. */
    private withWarehouseColumns(): any[] {
        const columns: any[] = [...this.columnDefs];
        const clientIndex = columns.findIndex((c: any) => c.field === 'client_name');
        columns.splice(clientIndex + 1, 0, {
            field: 'concept',
            headerName: 'Servicio de almacén',
            flex: 1.8,
            tooltipValueGetter: (p: any) => p.value
        });
        return columns;
    }

    ngOnInit(): void {
        if (this.source === 'warehouse') {
            // La factura dice a qué almacén pertenece, así que el listado sigue
            // al selector del header como el resto de la pantalla.
            this.columnDefs = this.withWarehouseColumns();
            this.warehouseSub = this.warehouseService.current$.subscribe(warehouse => {
                const changed = this.warehouseName !== null && this.warehouseName !== warehouse.name;
                this.warehouseName = warehouse.name;
                // La primera emisión sólo fija el almacén: el grid todavía no
                // está listo y es onGridReady quien dispara la primera carga.
                if (changed) this.loadInvoices();
            });
            // Las tarjetas de resumen se recalculan desde las filas; pedir el
            // resumen mensual real mezclaría facturas de transporte.
            return;
        }

        this.loadStats();
        if (this.showStampBalance) this.loadStampBalance();
    }

    ngOnDestroy(): void {
        this.warehouseSub?.unsubscribe();
    }

    loadStampBalance(fresh = false): void {
        this.stampBalanceLoading = true;
        this.invoiceService.stampBalance(fresh).subscribe({
            next: (resp) => {
                this.stampBalance = {
                    stamps_balance: Number(resp?.stamps_balance ?? 0),
                    is_unlimited: !!resp?.is_unlimited,
                    level: resp?.level ?? 'green'
                };
                this.stampBalanceLoading = false;
            },
            error: () => {
                this.stampBalance = null;
                this.stampBalanceLoading = false;
            }
        });
    }

    onGridReady(params: any) {
        if (params?.api) this.gridApi = params.api;
        this.loadInvoices();
    }

    loadStats() {
        this.invoiceService.monthlyStats().subscribe({
            next: (resp) => {
                this.stats = {
                    total_amount: Number(resp?.total_amount ?? 0),
                    issued_count: Number(resp?.issued_count ?? 0),
                    pending_amount: Number(resp?.pending_amount ?? 0),
                    paid_count: Number(resp?.paid_count ?? 0),
                    month: resp?.month ?? ''
                };
                this.monthLabel = this.formatMonthLabel(this.stats.month);
            }
        });
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
    }

    private formatMonthLabel(ym: string): string {
        if (!ym) return '';
        const [year, month] = ym.split('-').map(Number);
        const date = new Date(year, (month || 1) - 1, 1);
        const label = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    loadInvoices() {
        this.spinner.show();
        const filters = {
            search: this.searchText || undefined,
            issued_at: this.createdAt || undefined,
            status: this.statusFilter || undefined,
            payment_status: this.paymentStatusFilter || undefined,
            payment_method: this.paymentMethodFilter || undefined
        };
        const rows$: Observable<any[]> = this.source === 'warehouse'
            ? this.warehouseInvoiceService.list({ ...filters, warehouse: this.warehouseName || undefined })
            : this.invoiceService.list(filters);

        rows$.subscribe({
            next: (resp) => {
                this.rowData = resp || [];
                this.computeStatsFromRows();
                this.spinner.hide();
            },
            error: () => {
                this.rowData = [];
                this.computeStatsFromRows();
                this.spinner.hide();
            }
        });
    }

    private computeStatsFromRows() {
        const rows = this.rowData || [];
        let totalAmount = 0;
        let issuedCount = 0;
        let pendingAmount = 0;
        let paidCount = 0;

        for (const r of rows) {
            const status = String(r?.status || '');
            const paymentStatus = String(r?.payment_status || '');
            const total = Number(r?.total || 0);
            const isCancelled = status === 'Cancelada';

            if (!isCancelled) {
                totalAmount += total;
                issuedCount += 1;
                if (paymentStatus === 'Pagado' || status === 'Pagada') {
                    paidCount += 1;
                } else {
                    pendingAmount += total;
                }
            }
        }

        this.stats = {
            ...this.stats,
            total_amount: totalAmount,
            issued_count: issuedCount,
            pending_amount: pendingAmount,
            paid_count: paidCount
        };
        this.monthLabel = this.hasActiveFilters() ? 'Resultados filtrados' : 'Todos los resultados';
    }

    hasActiveFilters(): boolean {
        return !!(this.searchText || this.createdAt || this.statusFilter
            || this.paymentStatusFilter || this.paymentMethodFilter);
    }

    clearFilters() {
        if (!this.hasActiveFilters()) return;
        this.searchText = '';
        this.createdAt = '';
        this.createdAtDisplay = '';
        this.statusFilter = '';
        this.paymentStatusFilter = '';
        this.paymentMethodFilter = '';
        this.loadInvoices();
    }

    onCreatedAtInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 8);

        let masked = digits;
        if (digits.length > 4) {
            masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
            masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }

        this.createdAtDisplay = masked;
        input.value = masked;

        const previousIso = this.createdAt;

        if (digits.length === 8) {
            const dd = digits.slice(0, 2);
            const mm = digits.slice(2, 4);
            const yyyy = digits.slice(4, 8);
            const day = Number(dd);
            const month = Number(mm);
            const year = Number(yyyy);
            const candidate = new Date(year, month - 1, day);
            const isValid =
                candidate.getFullYear() === year &&
                candidate.getMonth() === month - 1 &&
                candidate.getDate() === day;
            this.createdAt = isValid ? `${yyyy}-${mm}-${dd}` : '';
        } else {
            this.createdAt = '';
        }

        if (this.createdAt !== previousIso) {
            this.onFilterChange();
        }
    }

    refreshGrid() {
        this.loadInvoices();
    }

    onSearch() {
        this.loadInvoices();
    }

    onFilterChange() {
        this.loadInvoices();
    }

    onSearchKeydown(evt: KeyboardEvent) {
        if (evt.key === 'Enter') this.loadInvoices();
    }

    handleRowAction(action: string, folio: string, _row?: any) {
        // Detrás de un folio de almacén no hay servicio en la BD: navegar
        // llevaría a una ficha vacía. Las acciones se habilitan cuando exista
        // el módulo de almacén en el backend.
        if (this.source === 'warehouse') return;

        switch (action) {
            case 'download':
            case 'detail':
            case 'pay':
            case 'view':
            default:
                this.router.navigate([`/service/list/folio-${folio}/view`]);
                break;
        }
    }
}
